import { skipToken } from '@reduxjs/toolkit/query/react';
import { Link } from 'react-router';
import { paths } from '@/config/paths';
import { Alert, ErrorState, Loading } from '@/shared/ui';
import { useAnalyticsModel, useAnalyticsRecords } from '../api/analyticsApi';
import { readValue, rowCurrency } from '../model/records';
import type { AnalyticsObject } from '../model/schema';
import { CellValue } from './CellValue';

/** `dl.kv` of every column of one reporting row. */
export function RecordFieldList({ object, record }: { object: AnalyticsObject; record: object }) {
  return (
    <dl className="kv">
      {object.columns.map((column) => (
        <div key={column.name}>
          <dt>{column.label}</dt>
          <dd>
            <CellValue
              column={column}
              value={readValue(record, column)}
              variant="detail"
              currency={rowCurrency(object, record)}
            />
          </dd>
        </div>
      ))}
    </dl>
  );
}

interface LinkedRecordPanelProps {
  /** Explorer key of the table, e.g. `api/teams` or `stripe/customers`. */
  objectKey: string;
  id: string;
}

/**
 * A single related record (team, Stripe customer, raw source row) inside a record form:
 * all its fields and a link to its own record page in the data explorer.
 */
export function LinkedRecordPanel({ objectKey, id }: LinkedRecordPanelProps) {
  const { model } = useAnalyticsModel();
  const [schema, slug] = objectKey.split('/');
  const object = model?.find(schema, slug);
  const idColumn = object?.entity?.idColumn;
  const { data, error, isLoading, refetch } = useAnalyticsRecords(
    object,
    idColumn
      ? {
          pageNumber: 1,
          pageSize: 1,
          sortDirection: 'asc',
          filters: [{ column: idColumn.name, operator: 'eq', value: id }],
        }
      : skipToken,
  );
  const record = data?.items[0];

  if (!model || isLoading) {
    return (
      <div className="record__note">
        <Loading />
      </div>
    );
  }
  if (!object?.entity) return <p className="empty">This record type is not available.</p>;
  if (error) {
    return (
      <div className="record__note">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }
  if (!record) {
    return (
      <div className="record__note">
        <Alert tone="error">
          No {object.entity.noun.toLowerCase()} with ID “{id}” was found in {object.schema}.
          {object.name}.
        </Alert>
      </div>
    );
  }
  return (
    <div className="record__body">
      <div className="record__section">
        <div className="card__head">
          <div className="card__titles">
            <h3 className="section-title">{object.entity.noun}</h3>
            <p className="card__sub">
              {object.schema}.{object.name} · ID {id}
            </p>
          </div>
          <Link className="btn btn--link" to={paths.analytics.record(object.key, id)}>
            Open {object.entity.noun.toLowerCase()} record
          </Link>
        </div>
        <RecordFieldList object={object} record={record} />
      </div>
    </div>
  );
}
